import { LitElement, html, css, nothing } from "lit";
import type { PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import type { Extension, Compartment } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import type { Diagnostic } from "@codemirror/lint";
import type { StreamParser, StringStream } from "@codemirror/language";

/** The languages bn-code can highlight. `plain` disables highlighting entirely. */
export type BnCodeLanguage = "yaml" | "sql" | "cel" | "plain";

/**
 * Diagnostic severities, in the linter's own vocabulary (03 §7.1): `error`
 * blocks save, `blocking-warning` blocks publish but allows save, `warning` is
 * informational. CodeMirror has no fifth level, so `blocking-warning` is drawn
 * as a warning — the severity word is spelled out in the tooltip so the reader
 * still learns that it blocks publish.
 */
export type BnCodeSeverity = "error" | "blocking-warning" | "warning" | "info" | "hint";

/**
 * One finding from the control-plane policy linter (`SC-F529`), positioned as a
 * character range over the document bn-code is showing.
 *
 * `ruleId` is the linter's stable rule identifier (`L01`…`L34`). It is rendered
 * as a chip in front of the message and is what
 * {@link BnCode.diagnostics | the click event} carries back to the page, so the
 * page can scroll the offending *form field* into view — the YAML is read-only,
 * so the fix never happens in the text.
 */
export interface BnCodeDiagnostic {
  /** Start offset, in characters, into `value`. */
  from: number;
  /** End offset. May equal `from`; a range that covers text reads better. */
  to: number;
  severity: BnCodeSeverity;
  message: string;
  /** The linter rule that produced this finding, e.g. `"L03"`. */
  ruleId?: string;
}

/** `detail` of the `bn-code-change` event. */
export interface BnCodeChangeDetail {
  value: string;
}

/** `detail` of the `bn-code-diagnostic-click` event. */
export interface BnCodeDiagnosticClickDetail {
  ruleId?: string;
  from: number;
  to: number;
  severity: BnCodeSeverity;
  message: string;
}

/**
 * The CodeMirror modules bn-code needs, as one lazily-resolved bundle. Typed
 * with `typeof import(...)` so the types are erased at build time and the only
 * thing esbuild sees is the dynamic `import()` calls in {@link loadCodeMirror}.
 */
interface CodeMirrorBundle {
  state: typeof import("@codemirror/state");
  view: typeof import("@codemirror/view");
  language: typeof import("@codemirror/language");
  commands: typeof import("@codemirror/commands");
  search: typeof import("@codemirror/search");
  autocomplete: typeof import("@codemirror/autocomplete");
  lint: typeof import("@codemirror/lint");
  highlight: typeof import("@lezer/highlight");
}

/**
 * Module-level cache: a page showing twelve rendered manifests loads the editor
 * once, and a failed load is not retried in a loop by every instance.
 */
let cmBundle: Promise<CodeMirrorBundle> | null = null;

function loadCodeMirror(): Promise<CodeMirrorBundle> {
  cmBundle ??= Promise.all([
    import("@codemirror/state"),
    import("@codemirror/view"),
    import("@codemirror/language"),
    import("@codemirror/commands"),
    import("@codemirror/search"),
    import("@codemirror/autocomplete"),
    import("@codemirror/lint"),
    import("@lezer/highlight"),
  ]).then(([state, view, language, commands, search, autocomplete, lint, highlight]) => ({
    state,
    view,
    language,
    commands,
    search,
    autocomplete,
    lint,
    highlight,
  }));
  return cmBundle;
}

/* ── CEL ──────────────────────────────────────────────────────────────────
   CEL has no CodeMirror language package, so this is a stream tokenizer over
   the subset Sluice's environment can actually express. Sluice declares three
   root variables for conditions (`subject`, `request`, `query`) and a fourth
   for row filters (`row`) with no custom functions at all
   (sluice/internal/policy/cel.go:30-52), so the vocabulary below is complete
   for this product rather than for CEL in general.

   IT HIGHLIGHTS: line comments (`//`), single- and double-quoted strings
   including `r`/`b` prefixes and backslash escapes, decimal/hex/float/unsigned
   numbers, `true`/`false`/`null`, CEL's reserved words and `in`, the standard
   macros and builtins when they are called, field selections after a dot, and
   the arithmetic/logic/comparison/ternary operators.

   IT DOES NOT HIGHLIGHT, by design: triple-quoted and any other multi-line
   string (it is tokenized to end-of-line and the next line recovers), and it
   does no parsing at all — so it will not mark unbalanced parentheses, a
   non-boolean result, or an expression that does not type-check. Those are the
   linter's job and arrive as `diagnostics`. It deliberately holds no policy
   opinion either: `request.*` is dead on the live path (`SLUICE-U12`, rule L03)
   and is still drawn as an ordinary variable, because encoding a lint rule in a
   highlighter would put the same judgement in two places. */

const CEL_RESERVED = new Set([
  "as",
  "break",
  "const",
  "continue",
  "else",
  "for",
  "function",
  "if",
  "import",
  "in",
  "let",
  "loop",
  "namespace",
  "package",
  "return",
  "var",
  "void",
  "while",
]);

const CEL_MACROS = new Set(["has", "all", "exists", "exists_one", "map", "filter"]);

const CEL_BUILTINS = new Set([
  "bool",
  "bytes",
  "contains",
  "double",
  "duration",
  "dyn",
  "endsWith",
  "getDate",
  "getDayOfMonth",
  "getDayOfWeek",
  "getDayOfYear",
  "getFullYear",
  "getHours",
  "getMilliseconds",
  "getMinutes",
  "getMonth",
  "getSeconds",
  "int",
  "matches",
  "size",
  "startsWith",
  "string",
  "timestamp",
  "type",
  "uint",
]);

interface CelState {
  /** True when the previous significant token was `.`, so the next identifier is a field. */
  afterDot: boolean;
}

const celParser: StreamParser<CelState> = {
  name: "cel",
  startState: () => ({ afterDot: false }),
  languageData: {
    commentTokens: { line: "//" },
    closeBrackets: { brackets: ["(", "[", "{", "'", '"'] },
  },
  token(stream: StringStream, celState: CelState): string | null {
    // Whitespace must not clear afterDot: `subject . id` is one selection.
    if (stream.eatSpace()) return null;

    const afterDot = celState.afterDot;
    celState.afterDot = false;

    if (stream.match("//")) {
      stream.skipToEnd();
      return "comment";
    }

    const quoted = stream.match(/^[rRbB]{0,2}(['"])/) as RegExpMatchArray | null;
    if (quoted) {
      const quote = quoted[1];
      let escaped = false;
      for (let ch = stream.next(); ch != null; ch = stream.next()) {
        if (ch === quote && !escaped) break;
        escaped = !escaped && ch === "\\";
      }
      return "string";
    }

    if (
      stream.match(/^0[xX][0-9a-fA-F]+[uU]?/) ||
      stream.match(/^(?:\d+\.\d*|\.\d+|\d+)(?:[eE][+-]?\d+)?[uU]?/)
    ) {
      return "number";
    }

    const word = stream.match(/^[A-Za-z_][A-Za-z0-9_]*/) as RegExpMatchArray | null;
    if (word) {
      const name = word[0];
      const called = stream.peek() === "(";
      if (called && (CEL_MACROS.has(name) || CEL_BUILTINS.has(name))) return "macroName";
      if (afterDot) return "propertyName";
      if (name === "true" || name === "false") return "bool";
      if (name === "null") return "null";
      if (CEL_RESERVED.has(name)) return "keyword";
      return "variableName";
    }

    if (stream.eat(".")) {
      celState.afterDot = true;
      return null;
    }

    if (stream.match(/^(?:&&|\|\||==|!=|<=|>=|[+\-*/%<>!?:])/)) return "operator";

    // Brackets, commas and anything unrecognised inherit the code colour.
    stream.next();
    return null;
  },
};

/** CodeMirror's four severities. `blocking-warning` folds into `warning`. */
const CM_SEVERITY: Record<BnCodeSeverity, Diagnostic["severity"]> = {
  error: "error",
  "blocking-warning": "warning",
  warning: "warning",
  info: "info",
  hint: "hint",
};

const LANGUAGE_LABEL: Record<BnCodeLanguage, string> = {
  yaml: "YAML",
  sql: "SQL",
  cel: "CEL expression",
  plain: "Text",
};

const DOWNLOAD_NAME: Record<BnCodeLanguage, string> = {
  yaml: "bundle.yaml",
  sql: "query.sql",
  cel: "expression.cel",
  plain: "code.txt",
};

const DOWNLOAD_TYPE: Record<BnCodeLanguage, string> = {
  yaml: "application/yaml;charset=utf-8",
  sql: "application/sql;charset=utf-8",
  cel: "text/plain;charset=utf-8",
  plain: "text/plain;charset=utf-8",
};

/**
 * bn-code is the product's only code surface: the read-only YAML view
 * (`SC-F602`), the CEL condition editors (`SC-F510`) and the SQL playground
 * (`SC-F712`) are all this element with a different `language`. Never use a
 * bare `<textarea>` — `SC-F529`'s value is in the inline diagnostics a text
 * area cannot draw (OD-12).
 *
 * **Lazy.** CodeMirror is pulled in by dynamic `import()` on first use, so the
 * main bundle carries no editor for a user who never opens one. Until it
 * resolves — and permanently, if it fails to resolve — the element renders the
 * text in a `<pre>` with the code typography and lists the diagnostics below
 * it, so the YAML view is readable and its findings are reachable either way.
 *
 * **Read-only by default**, because there is no raw-YAML editing anywhere in
 * this product (CLAUDE.md non-negotiable 2) and a forgotten attribute must fail
 * towards "cannot edit". An editor opts in with `.readonly=${false}`; the
 * `readonly` *attribute* cannot express false.
 *
 * **Theme.** The CodeMirror theme is rebuilt from the `--bino-code-*` and
 * `--bino-syntax-*` tokens read off this element, and rebuilt again whenever
 * `data-theme` on `<html>` or the OS colour scheme changes. Nothing here
 * branches on the theme itself.
 *
 *   <!-- the YAML view: read-only, copyable, downloadable -->
 *   <bn-code
 *     language="yaml"
 *     .value=${generation.yaml}
 *     .diagnostics=${lint.findings}
 *     filename="policies.d/sqlaccess.yaml"
 *     label="Rendered SqlAccessPolicy"
 *     @bn-code-diagnostic-click=${(e) => this.revealField(e.detail.ruleId)}
 *   ></bn-code>
 *
 *   <!-- the CEL condition field: editable, no chrome -->
 *   <bn-code
 *     language="cel"
 *     hide-toolbar
 *     hide-line-numbers
 *     .readonly=${false}
 *     .value=${condition.expression}
 *     @bn-code-change=${(e) => (condition.expression = e.detail.value)}
 *   ></bn-code>
 *
 * @fires bn-code-change - `CustomEvent<BnCodeChangeDetail>`, composed and
 *   bubbling, on every document change the user makes. Not fired when `value`
 *   is set from the outside. Never fired while `readonly`.
 * @fires bn-code-diagnostic-click - `CustomEvent<BnCodeDiagnosticClickDetail>`,
 *   composed and bubbling, when the reader activates a diagnostic's rule chip.
 *   Carries the `ruleId` so the page can scroll the responsible form control
 *   into view.
 */
@customElement("bn-code")
export class BnCode extends LitElement {
  /** The document text. Setting it replaces the editor's contents. */
  @property() value = "";

  /** Which grammar to highlight with. Only the one in use is downloaded. */
  @property() language: BnCodeLanguage = "plain";

  /**
   * Read-only presentation: no caret, no active-line highlight, a "Read-only"
   * badge. Defaults to `true`; an editor must set `.readonly=${false}`.
   */
  @property({ type: Boolean }) readonly = true;

  /** Linter findings to draw inline, gutter-marked and clickable. */
  @property({ attribute: false }) diagnostics: BnCodeDiagnostic[] = [];

  /** Accessible name for the code region. Defaults to the language. */
  @property() label = "";

  /** File name offered by the Download button. Defaults per language. */
  @property() filename = "";

  /** Hides the label / copy / download bar (for one-line CEL fields). */
  @property({ type: Boolean, attribute: "hide-toolbar" }) hideToolbar = false;

  /**
   * Hides the line-number gutter (for one-line CEL fields). Read once, when
   * the editor is created; changing it later does not reconfigure the gutter.
   */
  @property({ type: Boolean, attribute: "hide-line-numbers" }) hideLineNumbers = false;

  /** True once the editor has been created; until then the <pre> is showing. */
  @state() private ready = false;

  /** True when the editor chunk failed to load — the <pre> becomes permanent. */
  @state() private failed = false;

  /** Transient confirmation shown after Copy. */
  @state() private copied: "" | "ok" | "fail" = "";

  private cm?: CodeMirrorBundle;
  private view?: EditorView;
  private themeCompartment?: Compartment;
  private languageCompartment?: Compartment;
  private editableCompartment?: Compartment;
  private copiedTimer?: ReturnType<typeof setTimeout>;
  private themeObserver?: MutationObserver;
  private readonly darkQuery = window.matchMedia("(prefers-color-scheme: dark)");
  private readonly onThemeChange = () => this.refreshTheme();

  static override styles = css`
    :host {
      display: block;
    }
    [hidden] {
      display: none !important;
    }
    .wrap {
      display: flex;
      flex-direction: column;
      height: 100%;
      box-sizing: border-box;
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-code-bg);
      overflow: hidden;
    }
    .wrap:focus-within {
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }

    .toolbar {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-xs) var(--bino-space-sm);
      background: var(--bino-code-gutter-bg);
      border-bottom: 1px solid var(--bino-border);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .title {
      font-weight: 600;
      color: var(--bino-text-muted);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .spacer {
      flex: 1 1 auto;
    }
    .copy-status {
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      white-space: nowrap;
    }
    .pill {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      padding: 0.125rem 0.5rem;
      border-radius: var(--bino-radius-pill);
      font-size: var(--bino-font-size-xs);
      font-weight: 600;
      line-height: 1.4;
      white-space: nowrap;
      background: var(--bino-neutral-bg);
      color: var(--bino-neutral);
    }
    .pill.error {
      background: var(--bino-error-bg);
      color: var(--bino-error);
    }
    .pill.warning {
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
    }

    button {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      line-height: 1;
      padding: 0.3125rem 0.625rem;
      border-radius: var(--bino-radius-sm);
      border: 1px solid var(--bino-border-light);
      background: var(--bino-surface);
      color: var(--bino-text);
      cursor: pointer;
      transition:
        background var(--bino-transition-fast),
        border-color var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast);
    }
    button:hover {
      border-color: var(--bino-border-hover);
      background: var(--bino-surface-hover);
    }
    button:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }

    .body {
      position: relative;
      flex: 1 1 auto;
      min-height: 0;
      overflow: hidden;
    }
    .editor {
      height: 100%;
    }
    pre.plain {
      margin: 0;
      height: 100%;
      box-sizing: border-box;
      overflow: auto;
      padding: var(--bino-space-sm);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-code-text);
      background: var(--bino-code-bg);
      tab-size: 2;
    }
    pre.plain:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 2px var(--bino-focus-ring);
    }

    .status {
      padding: var(--bino-space-xs) var(--bino-space-sm);
      border-top: 1px solid var(--bino-border);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      background: var(--bino-code-gutter-bg);
    }

    .findings {
      list-style: none;
      margin: 0;
      padding: var(--bino-space-xs) var(--bino-space-sm) var(--bino-space-sm);
      border-top: 1px solid var(--bino-border);
      background: var(--bino-code-gutter-bg);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      max-height: var(--bino-panel-max-height);
      overflow: auto;
    }
    .findings li {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-xs) 0;
      color: var(--bino-text);
    }
    .rule {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      font-weight: 600;
      padding: 0.125rem 0.375rem;
      border-radius: var(--bino-radius-sm);
      border: 1px solid transparent;
      background: var(--bino-neutral-bg);
      color: var(--bino-neutral);
      cursor: pointer;
    }
    .rule.error {
      background: var(--bino-error-bg);
      color: var(--bino-error);
    }
    .rule.warning {
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
    }
    .rule:hover {
      border-color: var(--bino-border-hover);
    }
    .rule:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    // <html data-theme> is what the in-app toggle writes; the media query is
    // the OS preference tokens.css falls back to. Both must rebuild the theme.
    this.themeObserver = new MutationObserver(this.onThemeChange);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    this.darkQuery.addEventListener("change", this.onThemeChange);
    if (this.view) this.refreshTheme();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.themeObserver?.disconnect();
    this.themeObserver = undefined;
    this.darkQuery.removeEventListener("change", this.onThemeChange);
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  }

  override firstUpdated(): void {
    void this.boot();
  }

  override updated(changed: PropertyValues): void {
    if (!this.view || !this.cm) return;
    // pushValue compares before dispatching, so an editor-originated change
    // that round-trips through `value` costs a comparison and nothing else.
    if (changed.has("value")) this.pushValue();
    if (changed.has("language")) void this.pushLanguage();
    if (changed.has("readonly") || changed.has("label")) this.pushEditable();
    if (changed.has("diagnostics")) this.pushDiagnostics();
  }

  /* ── lifecycle ─────────────────────────────────────────────────────── */

  /** Loads CodeMirror and swaps the <pre> for a real editor. */
  private async boot(): Promise<void> {
    let cm: CodeMirrorBundle;
    try {
      cm = await loadCodeMirror();
    } catch {
      // Honest failure: the <pre> stays, and it is still readable and copyable.
      this.failed = true;
      return;
    }
    if (!this.isConnected) return;
    this.cm = cm;

    let languageExt: Extension;
    try {
      languageExt = await this.loadLanguage(cm);
    } catch {
      languageExt = [];
    }

    this.themeCompartment = new cm.state.Compartment();
    this.languageCompartment = new cm.state.Compartment();
    this.editableCompartment = new cm.state.Compartment();

    // Reveal the container before constructing the view: CodeMirror measures on
    // creation and measuring a display:none element yields a broken layout.
    this.ready = true;
    await this.updateComplete;
    const parent = this.renderRoot.querySelector<HTMLElement>(".editor");
    if (!parent) return;

    this.view = new cm.view.EditorView({
      parent,
      state: cm.state.EditorState.create({
        doc: this.value,
        extensions: [
          this.languageCompartment.of(languageExt),
          this.themeCompartment.of(this.buildTheme(cm)),
          this.editableCompartment.of(this.editableExtensions(cm)),
          ...this.staticExtensions(cm),
        ],
      }),
    });
    this.pushDiagnostics();
  }

  /** Only the grammar actually in use is fetched. */
  private async loadLanguage(cm: CodeMirrorBundle): Promise<Extension> {
    switch (this.language) {
      case "yaml":
        return (await import("@codemirror/lang-yaml")).yaml();
      case "sql": {
        const mod = await import("@codemirror/lang-sql");
        // Sluice parses with the pg_query backend, so PostgreSQL is the honest
        // dialect to highlight and complete against.
        return mod.sql({ dialect: mod.PostgreSQL, upperCaseKeywords: false });
      }
      case "cel":
        return cm.language.StreamLanguage.define(celParser);
      case "plain":
        return [];
    }
  }

  /** Extensions that never change for the lifetime of the view. */
  private staticExtensions(cm: CodeMirrorBundle): Extension[] {
    const { view, language, commands, search, autocomplete, lint } = cm;
    return [
      this.hideLineNumbers ? [] : view.lineNumbers(),
      view.highlightSpecialChars(),
      view.drawSelection(),
      view.EditorView.lineWrapping,
      language.foldGutter(),
      language.bracketMatching(),
      language.indentOnInput(),
      commands.history(),
      autocomplete.closeBrackets(),
      autocomplete.autocompletion(),
      search.highlightSelectionMatches(),
      lint.lintGutter(),
      // Tab is deliberately NOT bound to indentation: it must keep moving focus
      // out of the editor, or the whole page becomes a keyboard trap.
      view.keymap.of([
        ...commands.defaultKeymap,
        ...commands.historyKeymap,
        ...search.searchKeymap,
        ...autocomplete.completionKeymap,
        ...lint.lintKeymap,
      ]),
      view.EditorView.updateListener.of((update) => {
        if (!update.docChanged) return;
        const next = update.state.doc.toString();
        if (next === this.value) return;
        this.value = next;
        this.dispatchEvent(
          new CustomEvent<BnCodeChangeDetail>("bn-code-change", {
            detail: { value: next },
            bubbles: true,
            composed: true,
          }),
        );
      }),
    ];
  }

  /** The half of the configuration that depends on `readonly` and `label`. */
  private editableExtensions(cm: CodeMirrorBundle): Extension {
    const { view, state } = cm;
    const attrs: Record<string, string> = { "aria-label": this.accessibleName() };
    if (this.readonly) {
      // A read-only region still has to be reachable and scrollable by keyboard.
      attrs["aria-readonly"] = "true";
      attrs["tabindex"] = "0";
    }
    return [
      state.EditorState.readOnly.of(this.readonly),
      view.EditorView.editable.of(!this.readonly),
      view.EditorView.contentAttributes.of(attrs),
      // The class the theme hangs the read-only treatment off (no caret), so
      // "you cannot type here" is visible and not only announced.
      this.readonly ? view.EditorView.editorAttributes.of({ class: "bn-readonly" }) : [],
      this.readonly ? [] : [view.highlightActiveLine(), view.highlightActiveLineGutter()],
    ];
  }

  /* ── theme ─────────────────────────────────────────────────────────── */

  /**
   * Reads one design token off this element. CodeMirror hands these to
   * StyleModule as concrete declarations, so `var()` chains are resolved here
   * rather than deferred; if the element is not yet styled the token reference
   * itself is returned, which still resolves through the cascade and still
   * never hard-codes a colour.
   */
  private token(name: string): string {
    return getComputedStyle(this).getPropertyValue(name).trim() || `var(${name})`;
  }

  /** Mirrors tokens.css exactly: the attribute wins, the media query is fallback. */
  private isDark(): boolean {
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "dark") return true;
    if (attr === "light") return false;
    return this.darkQuery.matches;
  }

  private refreshTheme(): void {
    if (!this.view || !this.cm || !this.themeCompartment) return;
    this.view.dispatch({
      effects: this.themeCompartment.reconfigure(this.buildTheme(this.cm)),
    });
  }

  private buildTheme(cm: CodeMirrorBundle): Extension {
    const t = (name: string) => this.token(name);
    const tags = cm.highlight.tags;

    const bg = t("--bino-code-bg");
    const text = t("--bino-code-text");
    const gutterBg = t("--bino-code-gutter-bg");
    const gutterText = t("--bino-code-gutter-text");
    const selection = t("--bino-code-selection");
    const cursor = t("--bino-code-cursor");
    const activeLine = t("--bino-code-active-line");
    const border = t("--bino-border");
    const surface = t("--bino-surface");
    const error = t("--bino-error");
    const warning = t("--bino-warning");
    const info = t("--bino-info");
    const neutral = t("--bino-neutral");
    const spaceXs = t("--bino-space-xs");
    const spaceSm = t("--bino-space-sm");
    const radiusSm = t("--bino-radius-sm");

    const theme = cm.view.EditorView.theme(
      {
        "&": {
          height: "100%",
          color: text,
          backgroundColor: bg,
          fontFamily: t("--bino-font-mono"),
          fontSize: t("--bino-font-size-base"),
        },
        "&.cm-focused": { outline: "none" },
        ".cm-content": {
          caretColor: cursor,
          padding: `${spaceSm} 0`,
        },
        ".cm-scroller": { fontFamily: "inherit" },
        ".cm-gutters": {
          backgroundColor: gutterBg,
          color: gutterText,
          border: "none",
          borderRight: `1px solid ${border}`,
        },
        ".cm-activeLine": { backgroundColor: activeLine },
        ".cm-activeLineGutter": { backgroundColor: activeLine, color: text },
        ".cm-foldPlaceholder": {
          backgroundColor: gutterBg,
          color: gutterText,
          border: `1px solid ${border}`,
        },
        "&.cm-focused .cm-cursor, .cm-cursor, .cm-dropCursor": { borderLeftColor: cursor },
        "&.bn-readonly .cm-cursor, &.bn-readonly .cm-dropCursor": { display: "none" },
        ".cm-selectionBackground, &.cm-focused .cm-selectionBackground, ::selection": {
          backgroundColor: selection,
        },
        ".cm-selectionMatch": { backgroundColor: selection },
        ".cm-searchMatch": { backgroundColor: selection, outline: `1px solid ${border}` },
        ".cm-matchingBracket, .cm-nonmatchingBracket": {
          backgroundColor: selection,
          outline: `1px solid ${border}`,
        },
        ".cm-panels": { backgroundColor: surface, color: text, borderColor: border },
        ".cm-panels.cm-panels-top": { borderBottom: `1px solid ${border}` },
        ".cm-panels.cm-panels-bottom": { borderTop: `1px solid ${border}` },
        ".cm-tooltip": {
          backgroundColor: surface,
          color: text,
          border: `1px solid ${border}`,
          borderRadius: radiusSm,
          boxShadow: t("--bino-shadow-dropdown"),
          fontFamily: t("--bino-font-sans"),
          fontSize: t("--bino-font-size-sm"),
        },
        ".cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]": {
          backgroundColor: t("--bino-surface-active"),
          color: t("--bino-active-text"),
        },
        // CodeMirror's own lint marks are SVG data URIs in fixed colours, which
        // would survive a theme change unchanged. Replace them with token-driven
        // underlines and markers.
        ".cm-lintRange": { backgroundImage: "none", textDecorationSkipInk: "none" },
        ".cm-lintRange-error": { textDecoration: `underline wavy ${error}` },
        ".cm-lintRange-warning": { textDecoration: `underline wavy ${warning}` },
        ".cm-lintRange-info": { textDecoration: `underline wavy ${info}` },
        ".cm-lintRange-hint": { textDecoration: `underline wavy ${neutral}` },
        ".cm-lint-marker": {
          backgroundImage: "none",
          borderRadius: "50%",
          width: "0.6em",
          height: "0.6em",
          margin: "0.2em 0 0 0.2em",
        },
        ".cm-lint-marker-error": { backgroundColor: error },
        ".cm-lint-marker-warning": { backgroundColor: warning },
        ".cm-lint-marker-info": { backgroundColor: info },
        ".cm-lint-marker-hint": { backgroundColor: neutral },
        ".cm-diagnostic": {
          padding: `${spaceXs} ${spaceSm}`,
          borderLeftWidth: "3px",
          borderLeftStyle: "solid",
        },
        ".cm-diagnostic-error": { borderLeftColor: error },
        ".cm-diagnostic-warning": { borderLeftColor: warning },
        ".cm-diagnostic-info": { borderLeftColor: info },
        ".cm-diagnostic-hint": { borderLeftColor: neutral },
        ".bn-diagnostic": { display: "flex", alignItems: "baseline", gap: spaceXs },
        ".bn-diagnostic-rule": {
          fontFamily: t("--bino-font-mono"),
          fontSize: t("--bino-font-size-xs"),
          fontWeight: "600",
          padding: "0.125rem 0.375rem",
          borderRadius: radiusSm,
          border: `1px solid ${border}`,
          backgroundColor: gutterBg,
          color: text,
          cursor: "pointer",
        },
        ".bn-diagnostic-rule:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px ${t("--bino-focus-ring")}`,
        },
      },
      { dark: this.isDark() },
    );

    const highlight = cm.language.HighlightStyle.define([
      { tag: [tags.comment, tags.lineComment, tags.blockComment, tags.docComment], color: t("--bino-syntax-comment"), fontStyle: "italic" },
      {
        tag: [
          tags.keyword,
          tags.controlKeyword,
          tags.definitionKeyword,
          tags.moduleKeyword,
          tags.operatorKeyword,
          tags.macroName,
          tags.self,
        ],
        color: t("--bino-syntax-keyword"),
      },
      { tag: [tags.string, tags.docString, tags.character, tags.attributeValue], color: t("--bino-syntax-string") },
      {
        tag: [tags.number, tags.integer, tags.float, tags.bool, tags.null, tags.atom, tags.unit],
        color: t("--bino-syntax-number"),
      },
      { tag: [tags.propertyName, tags.attributeName, tags.labelName], color: t("--bino-syntax-property") },
      { tag: tags.invalid, color: t("--bino-syntax-invalid") },
      // variableName, typeName, operator, punctuation and brackets intentionally
      // inherit --bino-code-text: there is no token for them, and inventing a
      // colour here is exactly what the token rule forbids.
    ]);

    return [theme, cm.language.syntaxHighlighting(highlight)];
  }

  /* ── pushing property changes into the view ────────────────────────── */

  private pushValue(): void {
    const view = this.view;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === this.value) return;
    view.dispatch({ changes: { from: 0, to: current.length, insert: this.value } });
  }

  private async pushLanguage(): Promise<void> {
    const cm = this.cm;
    const view = this.view;
    if (!cm || !view || !this.languageCompartment) return;
    let ext: Extension;
    try {
      ext = await this.loadLanguage(cm);
    } catch {
      ext = [];
    }
    if (!this.view) return;
    this.view.dispatch({ effects: this.languageCompartment.reconfigure(ext) });
  }

  private pushEditable(): void {
    if (!this.cm || !this.view || !this.editableCompartment) return;
    this.view.dispatch({
      effects: this.editableCompartment.reconfigure(this.editableExtensions(this.cm)),
    });
  }

  private pushDiagnostics(): void {
    const cm = this.cm;
    const view = this.view;
    if (!cm || !view) return;
    const len = view.state.doc.length;
    const mapped: Diagnostic[] = this.diagnostics.map((d) => {
      // The linter positions findings over the rendered bytes; clamp so a stale
      // or redacted offset degrades to a marker rather than throwing.
      const from = Math.max(0, Math.min(d.from, len));
      const to = Math.max(from, Math.min(d.to, len));
      return {
        from,
        to,
        severity: CM_SEVERITY[d.severity] ?? "info",
        message: d.message,
        // No `source`: the panel would print the rule id a second time, next to
        // the chip renderMessage draws — and only the chip is clickable.
        renderMessage: () => this.renderDiagnosticMessage(d),
      };
    });
    view.dispatch(cm.lint.setDiagnostics(view.state, mapped));
  }

  /**
   * The tooltip / panel body for one finding: the rule id as an activatable
   * chip in front of the message. Built as DOM rather than a Lit template
   * because CodeMirror owns this node.
   */
  private renderDiagnosticMessage(d: BnCodeDiagnostic): Node {
    const wrap = document.createElement("div");
    wrap.className = "bn-diagnostic";
    if (d.ruleId) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "bn-diagnostic-rule";
      chip.textContent = d.ruleId;
      chip.title = `Go to the field ${d.ruleId} applies to`;
      chip.addEventListener("click", () => this.emitDiagnosticClick(d));
      wrap.appendChild(chip);
    }
    const text = document.createElement("span");
    text.textContent =
      d.severity === "blocking-warning" ? `${d.message} (blocks publish)` : d.message;
    wrap.appendChild(text);
    return wrap;
  }

  private emitDiagnosticClick(d: BnCodeDiagnostic): void {
    this.dispatchEvent(
      new CustomEvent<BnCodeDiagnosticClickDetail>("bn-code-diagnostic-click", {
        detail: {
          ruleId: d.ruleId,
          from: d.from,
          to: d.to,
          severity: d.severity,
          message: d.message,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /* ── export affordances ────────────────────────────────────────────── */

  private accessibleName(): string {
    if (this.label) return this.label;
    const lang = LANGUAGE_LABEL[this.language];
    return this.readonly ? `${lang}, read only` : `${lang} editor`;
  }

  private async onCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.value);
      this.copied = "ok";
    } catch {
      this.copied = "fail";
    }
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => (this.copied = ""), 2000);
  }

  private onDownload(): void {
    const name = this.filename || DOWNLOAD_NAME[this.language];
    const blob = new Blob([this.value], { type: DOWNLOAD_TYPE[this.language] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name.split("/").pop() || name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  /* ── render ────────────────────────────────────────────────────────── */

  private renderToolbar() {
    const errors = this.diagnostics.filter((d) => d.severity === "error").length;
    const warnings = this.diagnostics.filter(
      (d) => d.severity === "warning" || d.severity === "blocking-warning",
    ).length;
    return html`
      <div class="toolbar">
        <span class="title">${this.label || LANGUAGE_LABEL[this.language]}</span>
        ${this.readonly ? html`<span class="pill">Read-only</span>` : nothing}
        ${errors ? html`<span class="pill error">${errors} error${errors === 1 ? "" : "s"}</span>` : nothing}
        ${warnings
          ? html`<span class="pill warning">${warnings} warning${warnings === 1 ? "" : "s"}</span>`
          : nothing}
        <span class="spacer"></span>
        <span class="copy-status" role="status" aria-live="polite"
          >${this.copied === "ok" ? "Copied" : this.copied === "fail" ? "Copy failed" : ""}</span
        >
        <button type="button" @click=${this.onCopy}>Copy</button>
        <button type="button" @click=${this.onDownload}>Download</button>
      </div>
    `;
  }

  /**
   * The findings list shown only when there is no editor to draw them inline —
   * `SC-F529`'s findings must never be invisible just because a chunk failed.
   */
  private renderFallbackFindings() {
    if (this.ready || this.diagnostics.length === 0) return nothing;
    return html`
      <ul class="findings">
        ${this.diagnostics.map(
          (d) => html`
            <li>
              ${d.ruleId
                ? html`<button
                    type="button"
                    class="rule ${d.severity === "error"
                      ? "error"
                      : d.severity === "warning" || d.severity === "blocking-warning"
                        ? "warning"
                        : ""}"
                    @click=${() => this.emitDiagnosticClick(d)}
                  >
                    ${d.ruleId}
                  </button>`
                : nothing}
              <span>${d.message}</span>
            </li>
          `,
        )}
      </ul>
    `;
  }

  override render() {
    return html`
      <div class="wrap">
        ${this.hideToolbar ? nothing : this.renderToolbar()}
        <div class="body">
          <pre
            class="plain"
            ?hidden=${this.ready}
            tabindex="0"
            role="region"
            aria-label=${this.accessibleName()}
          >
${this.value}</pre
          >
          <div class="editor" ?hidden=${!this.ready}></div>
        </div>
        ${this.ready
          ? nothing
          : html`<p class="status" role="status" aria-live="polite">
              ${this.failed
                ? "Syntax highlighting is unavailable — the text above is the complete document."
                : "Loading the editor…"}
            </p>`}
        ${this.renderFallbackFindings()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-code": BnCode;
  }
  interface HTMLElementEventMap {
    "bn-code-change": CustomEvent<BnCodeChangeDetail>;
    "bn-code-diagnostic-click": CustomEvent<BnCodeDiagnosticClickDetail>;
  }
}
